import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Infinite-loop responsive carousel.
 * Renders N visible cards at a time (N varies by viewport) and uses modulo
 * arithmetic so when the last slide is shown the first slide appears
 * immediately next to it — no "scroll back" jump.
 *
 * Props:
 *   - breakpoints: { base, sm, md, lg, xl } — slides visible at each width
 *   - autoPlay / autoPlayInterval (defaults to 15s like the Use Cases carousel)
 */
const useSlidesPerView = (bp) => {
  const compute = React.useCallback(() => {
    if (typeof window === 'undefined') return bp.base;
    const w = window.innerWidth;
    if (w >= 1280 && bp.xl) return bp.xl;
    if (w >= 1024 && bp.lg) return bp.lg;
    if (w >= 768 && bp.md) return bp.md;
    if (w >= 640 && bp.sm) return bp.sm;
    return bp.base;
  }, [bp]);
  const [n, setN] = useState(compute);
  useEffect(() => {
    const handler = () => setN(compute());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [compute]);
  return n;
};

const ResponsiveCarousel = ({
  children,
  breakpoints = { base: 1, sm: 2, md: 2, lg: 3, xl: 3 },
  ariaLabel = 'Carousel',
  dataTestId = 'responsive-carousel',
  autoPlay = true,
  autoPlayInterval = 15000,
}) => {
  const slides = React.Children.toArray(children);
  const slidesPerView = useSlidesPerView(breakpoints);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const trackRef = useRef(null);

  const total = slides.length;
  const safeIndex = total > 0 ? ((currentIndex % total) + total) % total : 0;

  // Visible windowed slice (with wraparound)
  const visibleSlides = Array.from({ length: Math.min(slidesPerView, total) }).map(
    (_, i) => ({ slide: slides[(safeIndex + i) % total], key: (safeIndex + i) % total })
  );

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || total <= slidesPerView) return;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % total);
    }, autoPlayInterval);
    return () => clearInterval(id);
  }, [isAutoPlaying, autoPlayInterval, total, slidesPerView]);

  const stopAuto = () => setIsAutoPlaying(false);

  const next = () => {
    stopAuto();
    setCurrentIndex((i) => (i + 1) % total);
  };
  const prev = () => {
    stopAuto();
    setCurrentIndex((i) => (i - 1 + total) % total);
  };
  const goTo = (idx) => {
    stopAuto();
    setCurrentIndex(idx);
  };

  const gapClass = 'gap-6';
  const trackWidthClass = (() => {
    // Card widths in the visible row are equal flex children — use grid-like flex
    switch (slidesPerView) {
      case 1: return 'basis-full';
      case 2: return 'basis-1/2';
      case 3: return 'basis-1/3';
      case 4: return 'basis-1/4';
      default: return 'basis-full';
    }
  })();

  const showControls = total > slidesPerView;

  return (
    <div className="relative" aria-label={ariaLabel} data-testid={dataTestId}>
      <div ref={trackRef} className={`flex ${gapClass}`}>
        {visibleSlides.map(({ slide, key }, displayIdx) => (
          <div
            key={`${key}-${displayIdx}`}
            className={`flex-none ${trackWidthClass} transition-opacity duration-500`}
            style={{ minWidth: 0 }}
          >
            {slide}
          </div>
        ))}
      </div>

      {showControls && (
        <>
          {/* Desktop arrows */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous"
            data-testid={`${dataTestId}-prev`}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 shadow-2xl text-white items-center justify-center transition-all duration-200 z-10 ring-4 ring-white hover:scale-110 hover:shadow-orange-500/40"
          >
            <ChevronLeft className="h-7 w-7" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next"
            data-testid={`${dataTestId}-next`}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 shadow-2xl text-white items-center justify-center transition-all duration-200 z-10 ring-4 ring-white hover:scale-110 hover:shadow-orange-500/40"
          >
            <ChevronRight className="h-7 w-7" strokeWidth={3} />
          </button>

          {/* Mobile arrows + dots */}
          <div className="md:hidden mt-5 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              data-testid={`${dataTestId}-prev-mobile`}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center shadow-lg"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={3} />
            </button>
            <div className="flex items-center gap-2">
              {slides.map((_, idx) => ( // dots are positional; index is their stable identity
                <button
                  key={idx}
                  type="button"
                  onClick={() => goTo(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  data-testid={`${dataTestId}-dot-mobile-${idx}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === safeIndex ? 'w-8 bg-blue-600' : 'w-2 bg-slate-300 hover:bg-blue-400'
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={next}
              aria-label="Next"
              data-testid={`${dataTestId}-next-mobile`}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center shadow-lg"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={3} />
            </button>
          </div>

          {/* Desktop dot indicators */}
          <div className="hidden md:flex justify-center items-center gap-2 mt-8">
            {slides.map((_, idx) => ( // dots are positional; index is their stable identity
              <button
                key={idx}
                type="button"
                onClick={() => goTo(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                data-testid={`${dataTestId}-dot-${idx}`}
                className={`transition-all duration-300 rounded-full ${
                  idx === safeIndex
                    ? 'w-8 h-2 bg-blue-600 shadow-md'
                    : 'w-2 h-2 bg-slate-300 hover:bg-blue-400 hover:scale-125'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ResponsiveCarousel;
